import { useMemo } from "react";
import {
  queriesAccounts,
  queriesInternalAccounts,
  queriesTaxWithholdings
} from "@query";
import { useAccountAccess } from "@hooks/useAccountAccess";
import { useSession } from "@hooks/useSession";
import { useQueryPrimaryComplianceEntity } from "../../../shared/hooks/useQueryPrimaryComplianceEntity";
import { selectorProposeTaxWithholdings } from "../selectors/selectorProposeTaxWithholdings";

/**
 * The v3 data behind "should we nudge this account about tax withholdings",
 * shared by the two invoicing alerts and the dashboard promotion.
 *
 * `hasSetRate` is the discriminator the two alerts split on. v1 used
 * `member.profile.taxInfo.incomeEstimateForYear` — "has been through the rate
 * flow". The saved `withholdingRate` carries the same meaning: it is only ever
 * written by the rate screen, and it is the number the alerts quote.
 */
export const useProposeTaxWithholdings = () => {
  const { accountId } = useSession();
  const { hasScope } = useAccountAccess();

  const queryAccount = queriesAccounts.getAccount.useQuery(
    { accountId },
    { enabled: !!accountId }
  );
  const queryMonitoredEntity = useQueryPrimaryComplianceEntity();
  const queryInternalAccounts =
    queriesInternalAccounts.listInternalAccounts.useQuery();
  const querySettings =
    queriesTaxWithholdings.getTaxWithholdingSettings.useQuery();

  const propose = useMemo(
    () =>
      selectorProposeTaxWithholdings({
        account: queryAccount.data,
        monitoredEntity: queryMonitoredEntity.data,
        internalAccounts: queryInternalAccounts.data?.data,
        isTaxWithholdingEnabled: querySettings.data?.isEnabled,
        // v1 gated on the user having a Payee platform context; the scope the
        // side nav uses for the same "this is a contractor" question is the v3
        // stand-in.
        hasWithholdingsAccess: hasScope("bookkeeping.withholdingTransaction")
      }),
    [
      queryAccount.data,
      queryMonitoredEntity.data,
      queryInternalAccounts.data,
      querySettings.data,
      hasScope
    ]
  );

  return {
    propose,
    hasSetRate: !!querySettings.data?.withholdingRate
  };
};
